import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { HUB_UPDATES_QUERY } from "../api/documents";
import { useHubScope } from "../state/HubScopeContext";
import {
  insightLabelWithoutTerritory,
  territoryLabelFromInsight,
} from "../utils/insight-territory";

type HubUpdatesPanelProps = {
  readonly open: boolean;
  readonly onClose: () => void;
};

type InsightRow = {
  id: string;
  label: string;
  severity?: string | null;
  kind?: string | null;
  algorithmOrQuery?: string | null;
  createdAt?: string | null;
};

/**
 * Right-hand activity drawer (hanfani-inbox ActivitySidebar pattern):
 * backdrop + fixed panel, Escape / overlay to dismiss.
 */
export function HubUpdatesPanel({ open, onClose }: HubUpdatesPanelProps) {
  if (!open) return null;
  return <HubUpdatesPanelBody onClose={onClose} />;
}

function HubUpdatesPanelBody({ onClose }: { readonly onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";
  const { landscapeLabel, queryVars } = useHubScope();

  const { data, loading, refetch } = useQuery(HUB_UPDATES_QUERY, {
    variables: {
      territoryIds: queryVars.territoryIds,
      dateFrom: queryVars.dateFrom,
      dateTo: queryVars.dateTo,
    },
    fetchPolicy: "cache-and-network",
  });

  const insights = (data?.dashboardInsights ?? []) as InsightRow[];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="nbs-hub-drawer-root" data-testid="hub-updates-panel">
      <button
        type="button"
        className="nbs-hub-drawer__backdrop"
        aria-label={t("hub.overview.closeUpdates")}
        onClick={onClose}
      />
      <aside
        className="nbs-hub-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hub-updates-title"
      >
        <header className="nbs-hub-drawer__head">
          <div className="nbs-hub-drawer__title-wrap">
            <svg
              className="nbs-hub-drawer__title-ico"
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <path d="M6 9a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9Z" />
              <path d="M10 20a2 2 0 0 0 4 0" />
            </svg>
            <div>
              <h2 id="hub-updates-title" className="nbs-hub-drawer__title">
                {t("hub.overview.updatesTitle")}
              </h2>
              <p className="nbs-hub-drawer__sub">{t("hub.overview.updatesSub")}</p>
            </div>
          </div>
          <div className="nbs-hub-drawer__head-actions">
            <button
              type="button"
              className="nbs-hub__icon-btn"
              aria-label={t("hub.overview.refreshUpdates")}
              onClick={() => void refetch()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-2.6-6.3" />
                <path d="M21 3v6h-6" />
              </svg>
            </button>
            <button
              type="button"
              className="nbs-hub__icon-btn"
              data-testid="hub-updates-close"
              aria-label={t("hub.overview.closeUpdates")}
              onClick={onClose}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </header>

        <div className="nbs-hub-drawer__body">
          {loading && insights.length === 0 ? (
            <p className="nbs-hub-muted nbs-hub-drawer__empty">{t("hub.overview.loading")}</p>
          ) : insights.length === 0 ? (
            <div className="nbs-hub-drawer__empty-state">
              <p className="nbs-hub-drawer__empty-title">{t("hub.overview.noInsights")}</p>
              <p className="nbs-hub-muted">{t("hub.overview.updatesEmptyHint")}</p>
            </div>
          ) : (
            <ul className="nbs-hub-drawer__list">
              {insights.map((item) => {
                const territory = territoryLabelFromInsight(
                  item.label,
                  item.algorithmOrQuery,
                  landscapeLabel,
                );
                const body = insightLabelWithoutTerritory(item.label, territory);
                return (
                  <li
                    key={item.id}
                    className="nbs-hub-drawer__row"
                    data-severity={item.severity ?? "info"}
                  >
                    <span className="nbs-hub-drawer__marker" aria-hidden="true" />
                    <div className="nbs-hub-drawer__row-body">
                      <p className="nbs-hub-drawer__headline">{body}</p>
                      <div className="nbs-hub-drawer__meta">
                        <span>
                          {item.createdAt
                            ? new Intl.DateTimeFormat(locale, {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }).format(new Date(item.createdAt))
                            : (item.kind ?? "signal")}
                        </span>
                        {territory ? (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{territory}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
